import React, { useState } from 'react';
import { useRouter } from 'next/router';
import { cn, cx, getUserPermissions, hasPermission } from '@/utils';
import SvgHistory from '@common/Icons/History';
import SidebarHistory from '@common/SideMenu/SidebarHistory';
import { Popover, PopoverContent, PopoverHandler } from '@material-tailwind/react';
import { useHistoryStore } from '@utils/store/store';
import { MdMenu } from 'react-icons/md';

import { contentManagementPermissionsKeysType } from '@/utils/constants';
import ButtonBack from '@/components/common/ButtonBack/ButtonBack';
import { ButtonWithLogo } from '@/components/common/ButtonWithLogo/ButtonWithLogo';
import ScvComputer from '@/components/common/Icons/Computer';
import CrossIcon from '@/components/common/Icons/CrossIcon';
import SvgEye from '@/components/common/Icons/Eye';
import SvgTimesCircle from '@/components/common/Icons/TimesCircle';
import Modal from '@/components/common/Modals/Modal';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../../../common/DropdownMenu';
import SvgArrow from '../../../../common/Icons/Arrow';
import { Button } from '../../../../ui/Button';

type Props = {
  title: string | React.ReactNode;
  subtitle: string;
  section?: React.ReactNode;
  previsualize?: React.ReactNode;
  addButton?: React.ReactNode;
  linkAddButton?: string;
  nameAddButton?: string;
  previsualizeSize?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl';
  showClose?: boolean;
  onPrevisualize?: () => void;
  CreatePermissionsKey?: contentManagementPermissionsKeysType['pageBlockCreate'];
  tag?: React.ReactNode;
  previewModalClassName?: string;
  previewContentClassName?: string;
  hasHistory?: boolean;
  showBackButton?: boolean;
  backRoute?: string;
  asideSection?: React.ReactNode;
  containerClassName?: string;
  actions?: { name: string; onClick: () => void; icon?: React.ReactNode }[];
  pageDynamicId?: string; // Optional prop to pass to SidebarHistory
};

const TitleBlock = ({
  title,
  subtitle,
  section,
  previsualize,
  addButton,
  nameAddButton,
  linkAddButton,
  previsualizeSize = 'lg',
  showClose = true,
  onPrevisualize,
  CreatePermissionsKey,
  hasHistory = false,
  previewModalClassName,
  previewContentClassName,
  tag,
  showBackButton,
  backRoute,
  asideSection,
  containerClassName,
  actions,
  pageDynamicId,
}: Props) => {
  const [sectionVisible, setSectionVisible] = useState(false);
  const [prevVisible, setPrevVisible] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(false);
  const [hovered, setHovered] = useState(false);
  const triggers = {
    onclick: () => setSectionVisible(!sectionVisible),
    onMouseEnter: () => setHovered(true),
    onMouseLeave: () => setHovered(false),
  };
  const { history } = useHistoryStore();

  const openPrevisualization = () => {
    if (onPrevisualize) {
      onPrevisualize();
    }
    setPrevVisible(true);
  };

  const closePrevisualization = () => {
    setPrevVisible(false);
  };

  const { push } = useRouter();

  return (
    <div className={cn('mb-5 mt-2', containerClassName)}>
      <div className="flex flex-row items-center justify-between">
        <div className="flex flex-row items-center gap-x-4 py-3">
          {(showBackButton || backRoute) && <ButtonBack route={backRoute} hideLabel />}
          <h2 className="headline-h2 inline-block max-w-[600px] overflow-hidden overflow-ellipsis whitespace-nowrap  text-primary">
            {title}
          </h2>
          {tag && tag}
        </div>

        <div className="flex flex-row items-center space-x-2">
          {asideSection && asideSection}
          {actions && actions.length > 0 && (
            <DropdownMenu open={openDropdown} onOpenChange={setOpenDropdown}>
              <DropdownMenuTrigger asChild>
                {/* <ButtonWithLogo nameButton="Acciones" disabled={!!history?.id} /> */}

                <Button
                  variant={'default'}
                  color={'primary'}
                  className={cn('flex h-9 items-center gap-2.5 text-sm')}
                >
                  Acciones sobre el contenido
                  <SvgArrow
                    className={cn('size-[12px] rotate-90 transition-transform', {
                      '-rotate-90': openDropdown,
                    })}
                  />
                </Button>
              </DropdownMenuTrigger>

              <DropdownMenuContent align="start" side="bottom" className="min-w-[220px]">
                {CreatePermissionsKey &&
                  actions &&
                  hasPermission(getUserPermissions(), CreatePermissionsKey) &&
                  addButton && (
                    // <ButtonWithLogo
                    //   onClick={() => push(`${linkAddButton}`)}
                    //   logo={<CrossIcon />}
                    //   nameButton={nameAddButton}
                    // />
                    <DropdownMenuItem onClick={() => push(`${linkAddButton}`)}>
                      <CrossIcon className="h-4 w-4 text-primary" />
                      {nameAddButton}
                    </DropdownMenuItem>
                  )}
                {actions.map((action, index) => (
                  <DropdownMenuItem key={index} onClick={action.onClick}>
                    <div className="flex items-center gap-2">
                      {action.icon && action.icon}
                      {action.name}
                    </div>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          {CreatePermissionsKey &&
            !actions &&
            hasPermission(getUserPermissions(), CreatePermissionsKey) &&
            addButton && (
              <ButtonWithLogo
                onClick={() => push(`${linkAddButton}`)}
                logo={<CrossIcon />}
                nameButton={nameAddButton}
              />
            )}

          {(section || previsualize) && (
            <div className="flex flex-row items-center gap-2">
              {hasHistory && (
                <SidebarHistory pageDynamicId={pageDynamicId}>
                  <button>
                    <SvgHistory width={18} height={15} className={'text-marine'} />
                  </button>
                </SidebarHistory>
              )}
              {section ? (
                <Popover
                  placement="bottom"
                  handler={setSectionVisible}
                  open={sectionVisible && section != undefined}
                >
                  <PopoverHandler {...triggers}>
                    <div className="flex items-center">
                      <div
                        role="button"
                        className="body-text1 flex cursor-pointer items-center gap-2 rounded-[100px] border-[2px] border-primary bg-white px-6 py-2 capitalize text-primary transition-all hover:bg-primary hover:text-white"
                      >
                        <ScvComputer
                          width={20}
                          height={20}
                          className={cx(hovered ? 'text-white' : 'text-primary')}
                        />
                        {'Ver sección'}
                      </div>
                    </div>
                  </PopoverHandler>
                  <PopoverContent className="shadow-lg">
                    <div className="px-10 py-4">{section}</div>
                  </PopoverContent>
                </Popover>
              ) : null}
              {previsualize ? (
                <>
                  <ButtonWithLogo
                    onClick={openPrevisualization}
                    nameButton={'Previsualizar'}
                    logo={<SvgEye width={20} height={20} className="text-white" />}
                    disabled={!!history?.id}
                  />
                  <Modal
                    open={prevVisible && previsualize != undefined}
                    dismiss={{ enabled: false }}
                    handler={setPrevVisible}
                    size={previsualizeSize}
                    className={cx('outline-none', previewModalClassName)}
                  >
                    <div
                      className={cx(
                        'relative max-h-[700px] w-full min-w-full overflow-hidden overflow-y-auto rounded-md outline-none',
                        previewContentClassName
                      )}
                    >
                      <a
                        className="z-base absolute end-[20px] top-[20px] cursor-pointer"
                        onClick={closePrevisualization}
                      >
                        <SvgTimesCircle width={38} height={38} color="#00000050" />
                      </a>
                      <div className="relative">
                        <div className="relative flex-grow">{previsualize}</div>
                        {showClose ? (
                          <div className="flex flex-col py-[10px]">
                            <div className="flex items-center justify-center">
                              <ButtonWithLogo
                                onClick={closePrevisualization}
                                nameButton={'Cerrar'}
                                className="confirm-button !px-14"
                              />
                            </div>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </Modal>
                </>
              ) : null}
            </div>
          )}
        </div>
      </div>

      <label className="body-text1 text-tundora">{subtitle}</label>
    </div>
  );
};

export default TitleBlock;
